import React from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { useGetNews } from '../api/NewsQueueApi';
import ArticleView from './ArticleView';
import ArticlePanel from './ArticleView/components/ArticlePanel';

export default function ArticleViewController({ articleId, onClose, locksById, items = [], deskShortcut = null }) {
    const intl = useIntl();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const query = useGetNews({
        parameters: articleId ? { news_id: articleId } : undefined,
        enabled: Boolean(articleId),
    });

    if (!articleId) return null;

    const loading = query.isPending || query.isLoading;
    const fetchedArticle = query.data?.result;
    const feedArticle = items.find(item => String(item?.id) === String(articleId));
    const article = fetchedArticle ?? (loading ? feedArticle : null);
    // Feed metadata opens the panel immediately, but only a successful lookup enables actions.
    if (article && !query.isError) {
        const lock = locksById?.get(String(article.id));
        const selected = lock
            ? { ...article, locked_by: lock.locked_by, lock_expires_at: lock.lock_expires_at }
            : article;
        return (
            <ArticlePanel open onClose={onClose} isMobile={isMobile}>
                <ArticleView key={articleId} article={selected} open onClose={onClose}
                    isArticleLoading={!fetchedArticle} panelManaged deskShortcut={deskShortcut} />
            </ArticlePanel>
        );
    }

    if (loading) {
        const content = (
            <Box sx={{ p: isMobile ? 3 : 0 }}>
                <Button onClick={onClose}>
                    {intl.formatMessage({ id: 'newsQueue.article.returnToFeed' })}
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
                    <CircularProgress size={24} />
                    <Typography role="status">
                        {intl.formatMessage({ id: 'newsQueue.article.loading' })}
                    </Typography>
                </Box>
            </Box>
        );
        return (
            <ArticlePanel open onClose={onClose} isMobile={isMobile}>
                {content}
            </ArticlePanel>
        );
    }
    const status = query.error?.status;
    const notFound = [400, 404, 422].includes(status);
    const accessDenied = [401, 403].includes(status);
    const messageId = notFound
        ? 'newsQueue.article.notFound'
        : accessDenied
            ? 'newsQueue.article.accessDenied'
            : 'newsQueue.article.loadError';

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{intl.formatMessage({ id: 'newsQueue.article.title' })}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography role="alert">
                        {intl.formatMessage({ id: messageId })}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                {!notFound && !accessDenied && (
                    <Button onClick={() => query.refetch()}>
                        {intl.formatMessage({ id: 'newsQueue.article.retry' })}
                    </Button>
                )}
                <Button onClick={onClose}>
                    {intl.formatMessage({ id: 'newsQueue.article.returnToFeed' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
